import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { Checkbox, TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'

type Props = {
  value: AreaCode[]
  onChange: (areas: AreaCode[]) => void
  onSelectAll: () => void
}

/**
 * 業務エリアの選択 checkbox 群。
 * design/settings.html の業務エリア card 相当。
 *
 * - 「全エリアを選択」 **ボタン**: 全 10 エリアを一括で選択する。 全選択済みなら非活性
 *   ([DS/spec 差異] の経緯は下の JSX コメント参照)
 * - 各 area checkbox: flex-wrap で折り返し (= 全 10 エリア、 ラベルは canonical な
 *   convertAreaLabel = common:area.*)
 * - master と各 area は連動 (= 全部 checked で master checked、 一部 checked で indeterminate)
 */
export const BusinessAreasCheckboxes = ({
  value,
  onChange,
  onSelectAll,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const selectedCount = value.length
  const total = AREA_CODE_OPTIONS.length
  const isAllChecked = selectedCount === total

  const handleToggle = (areaCode: AreaCode, checked: boolean): void => {
    if (checked) {
      onChange([...value, areaCode])
    } else {
      onChange(value.filter((a) => a !== areaCode))
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px', // Spacing.2
        alignItems: 'center',
      }}
    >
      {/* 「全エリアを選択」 は **checkbox ではなくボタン**。

          業務エリアは最低 1 件必須で (spec AC-SPS-020 [P0])、 全解除方向の一括操作は
          提供しない (AC-SPS-017 [P0]、 2026-07-28 PdM 判断)。 つまりこの操作は
          **一方向にしか動かない**ので、 二値トグルである checkbox で表すと
          「外せるように見えて外せない」 嘘になる (2026-08-25 デザイナー判断)。
          ボタンなら「押すと全部選ぶ」 という一方向の意味がそのまま出せて、
          既に全選択なら非活性にするだけで済む。

          [spec 差異] AC-SPS-017 の文面は「マスターチェックボックス（全エリア）で全選択する」
          と部品名まで書いているが、 部品の選択は Des 裁量と解釈して差し替えた。
          spec 文面の追随は PdM へ確認する。

          非活性の理由は hover で出す ([[ButtonTooltip]] が器の span を挟む)。 */}
      <div style={{ flexBasis: '100%' }}>
        <ButtonTooltip
          content={
            isAllChecked
              ? t(
                  'page:settings.personal.businessAreas.allAreasAlreadySelected',
                )
              : undefined
          }
        >
          <TertiaryButton
            disabled={isAllChecked}
            onClick={onSelectAll}
            size="sm"
          >
            {t('page:settings.personal.businessAreas.selectAllAreas')}
          </TertiaryButton>
        </ButtonTooltip>
      </div>
      {AREA_CODE_OPTIONS.map((areaCode) => {
        const isLastArea = selectedCount === 1 && value.includes(areaCode)
        return (
          <Checkbox
            key={areaCode}
            aria-label={convertAreaLabel(areaCode)}
            checked={value.includes(areaCode)}
            disabled={isLastArea}
            onCheckedChange={(details: {
              checked: boolean | 'indeterminate'
            }) => {
              handleToggle(areaCode, details.checked === true)
            }}
          >
            {convertAreaLabel(areaCode)}
          </Checkbox>
        )
      })}
      {selectedCount === 1 && (
        <div style={{ flexBasis: '100%' }}>
          <Text color="Text.negativeMid" fontSize="sm">
            {t('page:settings.personal.businessAreas.minimumOneRequired')}
          </Text>
        </div>
      )}
    </div>
  )
}
